import type { AgenticAiMode, OperatorAttachedFile } from "@lib/agenticAi/types";
import type { PromptSourceType } from "@lib/agenticAiChat/types";
import type { FetchRequestError } from "@lib/errors";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type { SHARED_CONSTANTS_OPERATOR_AGENT_ERROR_CODES } from "@lib/shared_constants";
import sharedConstants from "@lib/shared_constants";

/** Fields shared by all AI agent provider request Props. */
export interface PostAiAgentBaseProps {
  /** current app group id */
  appGroupId: string;
  /** request id used to associate the request with the pusher stream */
  requestId: string;
  /** user message id - correlates all requests triggered by a single user prompt */
  userMessageId: string;
  /** chat session id */
  sessionId: string;
  /** mode this request is for */
  mode: AgenticAiMode;
  /** dashboard locale to use for agent/tool messages */
  locale?: string;
  /** browser locale from navigator.language or Accept-Language header */
  browserLocale?: string;
  /** which realtime transport the client is listening on, so the backend responds on the same channel */
  transport?: "dust" | "pusher";
  /**
   * Per-tab DUST session id (the derived session id from the per-tab /dust/auth
   * config) so the streamer publishes Operator events to this tab's
   * session-targeted MITE. Sent only when Operator direct mode is active; absent
   * otherwise so the backend falls back to the developer wildcard.
   */
  dustSessionId?: string;
  /** source of the user prompt (e.g. "dynamic_prompt" for button clicks) */
  promptSource?: PromptSourceType;
  /** when set, the backend returns a mock lorem-ipsum response of this character length instead of calling the provider API */
  mockResponseLength?: number;
  /**
   * Generic file attachments for the LLM to use as context (AIML-5357), e.g. the
   * campaign's persisted brief. Sent as `attached_files`; the backend validates each
   * descriptor and its streamer worker downloads the presigned URL. Include a file
   * only when it should newly enter the conversation -- the provider APIs carry it
   * forward on stateful follow-up turns. Empty when there is nothing new to attach.
   */
  attachedFiles: OperatorAttachedFile[];
}

/** Wire shape of one `attached_files` entry (see AiAgentController#validated_attached_file). */
export interface AttachedFileParam {
  url: string;
  file_name: string;
  mime_type: string;
  context: string;
}

export type PostAiAgentUnsupportedModeError = FetchUserEncodedError<
  SHARED_CONSTANTS_OPERATOR_AGENT_ERROR_CODES["UNSUPPORTED_MODE"],
  { mode: string }
>;

export type PostAiAgentAgentModeNotEnabledError = FetchUserEncodedError<
  SHARED_CONSTANTS_OPERATOR_AGENT_ERROR_CODES["AGENT_MODE_NOT_ENABLED"]
>;

export type PostAiAgentInvalidRequestIdError = FetchUserEncodedError<
  SHARED_CONSTANTS_OPERATOR_AGENT_ERROR_CODES["INVALID_REQUEST_ID"]
>;

export type PostAiAgentInputTooLargeError = FetchUserEncodedError<
  SHARED_CONSTANTS_OPERATOR_AGENT_ERROR_CODES["INPUT_TOO_LARGE"]
>;

export type PostAiAgentToolsListTooLargeError = FetchUserEncodedError<
  SHARED_CONSTANTS_OPERATOR_AGENT_ERROR_CODES["TOOLS_LIST_TOO_LARGE"]
>;

export type PostAiAgentUnknownModelError = FetchUserEncodedError<
  SHARED_CONSTANTS_OPERATOR_AGENT_ERROR_CODES["UNKNOWN_MODEL"],
  { model: string }
>;

export type PostAiAgentDuplicateToolNamesError = FetchUserEncodedError<
  SHARED_CONSTANTS_OPERATOR_AGENT_ERROR_CODES["DUPLICATE_TOOL_NAMES"]
>;

export type PostAiAgentRateLimitExceededError = FetchUserEncodedError<
  SHARED_CONSTANTS_OPERATOR_AGENT_ERROR_CODES["RATE_LIMIT_EXCEEDED"],
  { limit: number }
>;

export type PostAiAgentDailyCostCapExceededError = FetchUserEncodedError<
  SHARED_CONSTANTS_OPERATOR_AGENT_ERROR_CODES["DAILY_COST_CAP_EXCEEDED"]
>;

export type PostAiAgentKillSwitchOnError = FetchUserEncodedError<
  SHARED_CONSTANTS_OPERATOR_AGENT_ERROR_CODES["GEN_AI_KILL_SWITCH_ON"]
>;

export type PostAiAgentUnexpectedError = FetchUserEncodedError<
  SHARED_CONSTANTS_OPERATOR_AGENT_ERROR_CODES["UNEXPECTED_ERROR"]
>;

export type PostAiAgentErrors =
  | PostAiAgentUnsupportedModeError
  | PostAiAgentAgentModeNotEnabledError
  | PostAiAgentInvalidRequestIdError
  | PostAiAgentInputTooLargeError
  | PostAiAgentToolsListTooLargeError
  | PostAiAgentUnknownModelError
  | PostAiAgentDuplicateToolNamesError
  | PostAiAgentRateLimitExceededError
  | PostAiAgentDailyCostCapExceededError
  | PostAiAgentKillSwitchOnError
  | PostAiAgentUnexpectedError;

const aiAgentErrorCodes: ReadonlySet<string> = new Set(
  Object.values(sharedConstants.BRAZE_AGENTIC_AI.OPERATOR_AGENT_ERROR_CODES)
);

export const isPostAiAgentError = (error: FetchRequestError): error is PostAiAgentErrors => {
  return error instanceof FetchUserEncodedError && aiAgentErrorCodes.has(error.errorCode);
};
